/**
 * "Who's doing what" panel for the dashboard. Sorts members by last
 * activity time, descending. Shows a green dot if active within 5 minutes,
 * dim otherwise. Tooltip + secondary line carries today's event count.
 *
 * No real-time online/offline yet — that needs a "user_id → ws connection
 * state" map on the hub which we don't track. Last-active is a fine proxy.
 */
import { useQuery } from "@tanstack/react-query";
import { Users } from "lucide-react";
import { dashboardApi, type CompanyMember } from "../api/queries/companies";
import { formatRelative } from "../lib/time";

const ACTIVE_WINDOW_MS = 5 * 60 * 1000;

export function MembersPanel({ companyId }: { companyId: string }) {
  const q = useQuery({
    queryKey: ["members", companyId],
    queryFn: () => dashboardApi.members(companyId),
    refetchInterval: 15_000,
  });

  if (q.isLoading) {
    return (
      <div className="card p-3 h-full grid place-items-center">
        <span className="spinner" aria-hidden="true" />
      </div>
    );
  }

  const members = [...(q.data?.members ?? [])].sort((a, b) => {
    const la = a.last_active_at ?? 0;
    const lb = b.last_active_at ?? 0;
    return lb - la;
  });
  const now = Date.now();

  return (
    <div className="card p-3 h-full">
      <div className="flex items-center gap-2 mb-2">
        <Users size={14} className="text-tertiary" />
        <h3 className="text-[11px] font-medium tracking-wide uppercase text-tertiary">
          Team · {members.length}
        </h3>
      </div>
      <ul className="space-y-1">
        {members.map((m) => (
          <MemberRow key={m.user_id} member={m} now={now} />
        ))}
        {members.length === 0 && (
          <li className="text-[12px] text-tertiary">No team members.</li>
        )}
      </ul>
    </div>
  );
}

function MemberRow({ member, now }: { member: CompanyMember; now: number }) {
  const active =
    member.last_active_at != null && now - member.last_active_at < ACTIVE_WINDOW_MS;
  const initials = member.display_name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";

  return (
    <li className="flex items-center gap-2 text-[12px]">
      <div
        className={`grid place-items-center w-7 h-7 rounded-full text-[11px] font-medium shrink-0 ${
          active ? "bg-accent/20 text-accent" : "bg-sunken text-tertiary"
        }`}
        aria-hidden="true"
      >
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-primary truncate">
            {member.display_name}
          </span>
          {active && (
            <span
              className="inline-block w-1.5 h-1.5 rounded-full bg-status-running"
              title="Active in the last 5 minutes"
            />
          )}
          <span className="text-[10px] text-tertiary uppercase tracking-wide">
            {member.role}
          </span>
        </div>
        <div className="text-[11px] text-tertiary truncate">
          {member.last_active_at
            ? `Active ${formatRelative(member.last_active_at)} · ${member.events_today} today`
            : "No activity yet"}
        </div>
      </div>
    </li>
  );
}
